import CreateEditUserModal from '@components/organisms/user/CreateEditUserModal';
import { useDebounce } from '@hooks/useDebounce';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useToggleUserStatus,
} from '@hooks/api/users';
import { useOrganizations } from '@hooks/api/organizations';
import { UserRoles, UserStatus } from '@models/user.model';
import UserList from '@pages/admin/list';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { AccountCircleOutlined } from '@mui/icons-material';
import { Button } from '@components/atoms/Button';
import { useTranslation } from '@hooks/useTranslation';

interface UsersPageProps {}

const AdminPage: React.FC<UsersPageProps> = () => {
  // search
  const tActionMessages = useTranslation('actionMessages');
  const [search, setSearch] = useState<string>('');
  const debouncedSearchValue = useDebounce(search, 500);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };
  // filter
  const [filter, setFilter] = useState<string>('Active');
  const handleFilterChange = (filter: string) => {
    setFilter(filter);
    setPage(0);
  };
  // role filter
  const [roleFilter, setRoleFilter] = useState<UserRoles | null>(null);
  const handleRoleFilterChange = (role: UserRoles | null) => {
    setRoleFilter(role);
    setPage(0);
  };
  // organization filter
  const [organizationFilter, setOrganizationFilter] = useState<string | null>(
    null,
  );
  const handleOrganizationFilterChange = (organizationId: string | null) => {
    setOrganizationFilter(organizationId);
    setPage(0);
  };
  // createdAt preset filter (days back)
  const [createdAtDays, setCreatedAtDays] = useState<number | null>(null);
  const handleCreatedAtDaysChange = (days: number | null) => {
    setCreatedAtDays(days);
    setPage(0);
  };
  const createdAtFrom = createdAtDays
    ? new Date(Date.now() - createdAtDays * 24 * 60 * 60 * 1000).toISOString()
    : null;

  const handleClearFilters = () => {
    setFilter('Active');
    setRoleFilter(null);
    setOrganizationFilter(null);
    setCreatedAtDays(null);
    setSearch('');
    setPage(0);
  };

  // sort
  const [orderBy, setOrderBy] = useState<string | null>('name');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // pagination
  const [page, setPage] = useState<number>(0);
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setRowsPerPage(event.target.value as unknown as number);
    setPage(0);
  };

  // API hooks
  const usersQuery = useUsers({
    page,
    limit: rowsPerPage,
    search: debouncedSearchValue,
    filter,
    orderBy: orderBy || 'name',
    orderDirection,
    role: roleFilter,
    filterOrganizationId: organizationFilter,
    createdAtFrom,
  });

  // Fetch all organizations
  const organizationsQuery = useOrganizations({
    filter: 'ENABLED',
    limit: 100,
  });

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const toggleUserStatusMutation = useToggleUserStatus();

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const modalClose = (): void => {
    setShowCreateModal(false);
  };

  const handleAddUser = () => {
    setShowCreateModal(true);
  };

  const toggleEnableDisableUser = (userId: string, enable: boolean = false) => {
    if (!userId) return;
    toggleUserStatusMutation.mutate({ userId, enable });
  };

  const createUser = (
    organizationId: string,
    status: UserStatus | null,
    email: string,
    firstName: string,
    lastName: string,
    // Undefined on edit means "profile only": the memberships list in the modal
    // owns the roles, and updateUser skips the role mutation when it is absent.
    role: UserRoles | undefined,
    userId?: string,
  ) => {
    if (!email) {
      toast.error(tActionMessages.emailIsRequired);
      return;
    }

    // If is has a userId, it's an edit
    const isEdit = !!userId;

    if (isEdit && userId) {
      updateUserMutation.mutate(
        {
          userId,
          firstName,
          lastName,
          role,
          // Target org for the role assignment; also (re-)attaches users that
          // currently have no organization at all
          organizationId: organizationId || undefined,
        },
        {
          onSuccess: () => {
            setShowCreateModal(false);
          },
        },
      );
    } else if (status !== null) {
      // A role is only ever optional on edit, where it means "leave memberships
      // alone". A new user has to land somewhere with something.
      if (!role) {
        toast.error(tActionMessages.roleIsRequired);
        return;
      }
      createUserMutation.mutate(
        {
          organizationId,
          status,
          email,
          firstName,
          lastName,
          role,
        },
        {
          onSuccess: () => {
            setShowCreateModal(false);
          },
        },
      );
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <AccountCircleOutlined
            sx={{ fontSize: 28 }}
            className="text-orange-400 dark:text-orange-300"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Admin Users
          </h1>
        </div>
        <Button onClick={handleAddUser}>Add User</Button>
      </div>

      <UserList
        handleDisableUser={toggleEnableDisableUser}
        handleEnableUser={(id) => toggleEnableDisableUser(id, true)}
        handleEditUser={createUser}
        count={usersQuery.data?.count || 0}
        users={usersQuery.data?.users || []}
        organizations={organizationsQuery.data?.organizations || []}
        page={page}
        handleChangePage={handleChangePage}
        rowsPerPage={rowsPerPage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        search={search}
        handleSearchChange={handleSearchChange}
        orderDirection={orderDirection}
        orderBy={orderBy}
        handleRequestSort={handleRequestSort}
        filter={filter}
        handleFilterChange={handleFilterChange}
        roleFilter={roleFilter}
        handleRoleFilterChange={handleRoleFilterChange}
        organizationFilter={organizationFilter}
        handleOrganizationFilterChange={handleOrganizationFilterChange}
        createdAtDays={createdAtDays}
        handleCreatedAtDaysChange={handleCreatedAtDaysChange}
        handleClearFilters={handleClearFilters}
      />

      <CreateEditUserModal
        isOpen={!!showCreateModal}
        onClose={modalClose}
        createUser={createUser}
        organizations={organizationsQuery.data?.organizations || []}
        saving={createUserMutation.isPending || updateUserMutation.isPending}
      />
    </div>
  );
};

export default AdminPage;
